import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Haus-Regel „Auswahl statt Tippen" (Arben): admin-gepflegte Objekte werden AUSGEWÄHLT, nie
// getippt. Eine getippte Kennung ist nicht überprüfbar — sie läuft entweder in ein generisches
// 400 des Masters oder, schlimmer, auf ein FREMDES Objekt, das zufällig existiert.
//
// Dieses Primitiv ist die eine Optik und das eine Verhalten dafür: ein Knopf, der eine Liste
// mit Suchfeld aufklappt; Tippen filtert, Klick wählt und schliesst, Escape bricht ab.
//
// ⚠ Die testids sind bewusst fest verdrahtet (`<testId>-hinzufuegen|-suche|-option-<id>`) —
// die Aufrufer geben nur das Präfix. So heisst dasselbe Bedien-Element überall gleich, und ein
// Klickweg (Playwright) funktioniert an jeder Stelle nach demselben Muster.

export interface AuswahlEintrag {
  id: string;
  /** Die Zeile, die den Eintrag benennt — NIE eine blosse Kennung. */
  primaer: string;
  /** Zusatz zur Unterscheidung (Inhaber, Zustand, Quelle …). Wird mitdurchsucht. */
  sekundaer?: string;
}

export function AuswahlMitSuche({
  eintraege,
  onWaehlen,
  testId,
  oeffnenLabel,
  suchePlatzhalter,
  leerText,
  keinTrefferText = "Nichts passt zur Suche.",
  disabled = false,
}: {
  eintraege: AuswahlEintrag[];
  onWaehlen: (id: string) => void;
  /** Präfix der testids — siehe Kommentar oben. */
  testId: string;
  oeffnenLabel: string;
  suchePlatzhalter: string;
  /** Es gibt ÜBERHAUPT nichts zu wählen (etwas anderes als „die Suche trifft nichts"). */
  leerText: string;
  keinTrefferText?: string;
  disabled?: boolean;
}) {
  const [offen, setOffen] = useState(false);
  const [suche, setSuche] = useState("");

  const schliessen = () => {
    setOffen(false);
    setSuche("");
  };

  if (!offen) {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => setOffen(true)}
        data-testid={`${testId}-hinzufuegen`}
      >
        <Plus className="h-4 w-4" />
        {oeffnenLabel}
      </Button>
    );
  }

  const nadel = suche.trim().toLowerCase();
  const treffer = eintraege.filter(
    (e) =>
      e.primaer.toLowerCase().includes(nadel) || (e.sekundaer ?? "").toLowerCase().includes(nadel),
  );

  return (
    <div className="space-y-2 rounded-md border border-border p-2.5">
      <Input
        autoFocus
        value={suche}
        onChange={(e) => setSuche(e.target.value)}
        // Escape schliesst, ohne etwas zu wählen — die Liste darf keine Sackgasse sein.
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            schliessen();
          }
        }}
        placeholder={suchePlatzhalter}
        data-testid={`${testId}-suche`}
      />
      {treffer.length === 0 ? (
        // ⚠ Zwei verschiedene Aussagen: „es gibt nichts" vs. „die Suche trifft nichts". Wer sie
        // zusammenlegt, schickt jemanden die Rollen-Verwaltung reparieren, der nur zu eng
        // gesucht hat.
        <p className="text-[12.5px] text-text3" data-testid={`${testId}-leer`}>
          {eintraege.length === 0 ? leerText : keinTrefferText}
        </p>
      ) : (
        <ul className="max-h-40 space-y-1 overflow-auto">
          {treffer.map((e) => (
            <li key={e.id}>
              <button
                className="w-full rounded px-2 py-1 text-left text-[13px] hover:bg-surface-2"
                onClick={() => {
                  schliessen();
                  onWaehlen(e.id);
                }}
                data-testid={`${testId}-option-${e.id}`}
              >
                {e.primaer}
                {e.sekundaer && <span className="ml-2 text-[11.5px] text-text3">{e.sekundaer}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
